import { useState } from "react";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import PurgeTestsCard from "@/components/PurgeTestsCard";

// Admin gate: trust backend `is_admin` (source of truth = ADMIN_EMAIL env).

export default function AdminReconcile() {
  const { user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setResult(null);
    const clean = email.trim().toLowerCase();
    if (!clean) {
      setError("Enter a user email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(
        `/admin/reconcile-subscription?email=${encodeURIComponent(clean)}`
      );
      setResult(data);
      if (data.ok) {
        toast.success(`Reconciled ${clean} → ${data.plan || "pro"}`);
      } else {
        toast.warning(data.reason || "Nothing to reconcile");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-16" data-testid="admin-reconcile-page">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Admin · Recovery tool</div>
      <h1 className="font-serif text-5xl mt-3">Reconcile subscription.</h1>
      <p className="mt-4 text-sm" style={{ color: "var(--ink-soft)" }}>
        For users who paid on Stripe but never got their <strong>Maker Pro</strong> badge —
        usually because the webhook was dropped by a 308 redirect or hit before the user
        record was linked. Enter their email and we&apos;ll pull their active subscription
        from Stripe and stamp it onto their account.
      </p>

      <form onSubmit={onSubmit} className="mt-10 space-y-6">
        <div>
          <Label htmlFor="rec-email" className="text-xs uppercase tracking-widest">User email</Label>
          <Input
            id="rec-email"
            data-testid="reconcile-email"
            type="email"
            placeholder="maker@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2"
          />
        </div>
        {error && (
          <div
            data-testid="reconcile-error"
            className="flex items-start gap-2 text-sm rounded-md p-3"
            style={{ backgroundColor: "rgba(31,93,52,0.08)", color: "var(--terracotta)" }}
          >
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <div>{error}</div>
          </div>
        )}
        <Button
          type="submit"
          data-testid="reconcile-submit"
          disabled={busy}
          className="btn-primary py-6 px-8 rounded-full inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={busy ? "animate-spin" : ""} />
          {busy ? "Checking Stripe…" : "Reconcile now"}
        </Button>
      </form>

      {result && (
        <div
          data-testid="reconcile-result"
          className="mt-10 rounded-lg border p-5"
          style={{
            borderColor: result.ok ? "rgba(111,125,85,0.35)" : "rgba(0,0,0,0.12)",
            backgroundColor: result.ok ? "rgba(111,125,85,0.06)" : "rgba(0,0,0,0.03)",
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            {result.ok ? (
              <>
                <CheckCircle2 size={18} style={{ color: "var(--moss)" }} />
                <span className="font-medium" style={{ color: "var(--moss)" }}>
                  Reconciled successfully
                </span>
              </>
            ) : (
              <>
                <AlertCircle size={18} style={{ color: "var(--ink-soft)" }} />
                <span className="font-medium" style={{ color: "var(--ink-soft)" }}>
                  Nothing to reconcile
                </span>
              </>
            )}
          </div>
          {result.ok ? (
            <dl className="text-sm space-y-2">
              <div className="flex gap-3">
                <dt className="w-40 uppercase text-xs tracking-widest" style={{ color: "var(--ink-soft)" }}>Email</dt>
                <dd>{result.email}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-40 uppercase text-xs tracking-widest" style={{ color: "var(--ink-soft)" }}>Plan</dt>
                <dd>{result.plan}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-40 uppercase text-xs tracking-widest" style={{ color: "var(--ink-soft)" }}>Stripe customer</dt>
                <dd className="font-mono text-xs break-all">{result.stripe_customer_id}</dd>
              </div>
              <div className="flex gap-3">
                <dt className="w-40 uppercase text-xs tracking-widest" style={{ color: "var(--ink-soft)" }}>Subscription</dt>
                <dd className="font-mono text-xs break-all">{result.stripe_subscription_id}</dd>
              </div>
            </dl>
          ) : (
            <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
              {result.reason || "No active Stripe subscription found for that email."}
              &nbsp;This usually means the user hasn&apos;t paid yet — or they paid with a
              different email than the one on file here.
            </p>
          )}
        </div>
      )}

      <div className="mt-10 pt-6 border-t" style={{ borderColor: "rgba(0,0,0,0.08)" }}>
        <p className="text-xs" style={{ color: "var(--ink-soft)" }}>
          <strong>Note:</strong> preview and production databases are separate. To fix a
          real user&apos;s Pro badge on addvendtour.com, run this from the live site.
        </p>
      </div>

      <div className="mt-14">
        <PurgeTestsCard />
      </div>
    </div>
  );
}
